import { useCallback, useRef } from "react";
import { useNotify } from "react-admin";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import Clear from "@mui/icons-material/Clear";
import { SAFAI_ACCEPTED_TYPES, validateSafaiFile } from "./raza-safai-file";
import RazaSafaiImagePreview from "./RazaSafaiImagePreview";

type RazaSafaiUploadControlsProps = {
  safaiUrl: string | null;
  uploading?: boolean;
  disabled?: boolean;
  imageVariant?: "form" | "show";
  onSelectFile: (file: File) => void | Promise<void>;
  onClear?: () => void | Promise<void>;
};

export default function RazaSafaiUploadControls({
  safaiUrl,
  uploading = false,
  disabled = false,
  imageVariant = "form",
  onSelectFile,
  onClear,
}: RazaSafaiUploadControlsProps) {
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (!file) return;
      const validationError = validateSafaiFile(file);
      if (validationError) {
        notify(validationError, { type: "error" });
        return;
      }
      await onSelectFile(file);
    },
    [notify, onSelectFile]
  );

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          component="label"
          startIcon={uploading ? <CircularProgress size={18} /> : <PhotoCamera />}
          disabled={disabled || uploading}
        >
          {safaiUrl ? "Replace image" : "Upload Safai"}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept={SAFAI_ACCEPTED_TYPES.join(",")}
            onChange={handleChange}
          />
        </Button>
        {safaiUrl && onClear ? (
          <IconButton
            aria-label="Remove safai image"
            onClick={() => void onClear()}
            disabled={disabled || uploading}
            size="small"
          >
            <Clear />
          </IconButton>
        ) : null}
      </Box>
      {safaiUrl ? (
        <Box
          sx={{
            mt: 1.5,
            width: { xs: "100%", sm: "85%", md: "50%" },
            maxWidth: { md: 480 },
          }}
        >
          <RazaSafaiImagePreview src={safaiUrl} variant={imageVariant} />
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          PNG or JPEG, up to 5 MB
        </Typography>
      )}
    </>
  );
}
